import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import {
  ReactNode,
  useEffect,
  useReducer,
  useState
} from "react"
import { httpsCallable } from 'firebase/functions'
import { socket, functions } from "../index"
import { User } from "../providers/users"
import {
  useTheme,
  styles,
  stylesDark,
  Styles
} from "../providers/theme"
import {
  NoPhotographyRounded,
  RadioButtonUncheckedRounded,
  RadioButtonCheckedRounded,
  EditRounded,
} from "@mui/icons-material"
import FormControl from '@mui/material/FormControl'
import { useAuth } from "../providers/auth"
import PhoneInput from 'react-phone-number-input'
import useWidth from "../hooks/window"
import { LoadingButton } from "@mui/lab"
import { PropertyArea, MultiplePropertiesArea } from "./propertyArea"
import { useTranslationFiles } from "../providers/translationFiles"
import { useUsers } from "../providers/users"

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

interface UserCardState {
  availableSpeechTranslationFiles: string;
  availableWebInterfaceTranslationFiles: string;
  availableIBTGTranslationFiles: string;
  companies: string;
  company: string;
  serialNumbers: string;
  manufacturer: string;
  role: string;
  modalOpen: boolean;
  dialogOpen: boolean;
}

interface DialogState {
  dialogActionButtonLabel: string;
  title: string;
  description: string;
  property: string;
  inputType: string;
  inputLabel: string;
  inputRegex: string;
  saveButtonLoading: boolean;
  socketSignal: string;
  separator: string;
  itemsList: string[];
  itemValue: any;
  itemIsValid: boolean;
}

export default function UserCard({
  user,
  displayMode,
  distributors,
  manufacturersList
}: {
  user: User,
  displayMode: string,
  distributors: string[],
  manufacturersList: string[]
}) {
  const {currentRole, user: currentUser} = useAuth()
  const {mode} = useTheme()
  const {setSelectedUser} = useUsers()
  const {
    speechTranslationFilesFW2, 
    webInterfaceTranslationFilesFW2, 
    speechTranslationFilesFW3, 
    webInterfaceTranslationFilesFW3,
    IBTGTranslationFiles
  } = useTranslationFiles()
  const classesLight = useStylesLight(styles)
  const classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark
  const width = useWidth()
  const [userState, dispatchUserState] = useReducer((currentState: UserCardState, newState: Partial<UserCardState>) => ({
    ...currentState,
    ...newState
  }), {
    // User attributes
    availableSpeechTranslationFiles: user.availableSpeechTranslationFiles,
    availableWebInterfaceTranslationFiles: user.availableWebInterfaceTranslationFiles,
    availableIBTGTranslationFiles: user.availableIBTGTranslationFiles,
    companies: user.companies,
    company: user.company,
    serialNumbers: user.serialNumbers,
    manufacturer: user.manufacturer,
    role: user.role,
    // Modal
    modalOpen: false,
    // Dialog
    dialogOpen: false
  })
  const [dialogState, dispatchDialogState] = useReducer((currentState: DialogState, newState: Partial<DialogState>) => ({
    ...currentState,
    ...newState
  }), {
    dialogActionButtonLabel: '',
    title: '',
    description: '',
    property: '',
    inputType: '',
    inputLabel: '',
    inputRegex: '',
    saveButtonLoading: false,
    socketSignal: '',
    separator: '',
    itemsList: [],
    itemValue: '',
    itemIsValid: true
  })
  const [dialogInput, setDialogInput] = useState<ReactNode | null>(<></>)
  const [distributorsListPreload, setDistributorsListPreload] = useState<ReactNode[]>([])
  const [speechTranslationFilesPreload, setSpeechTranslationFilesPreload] = useState<ReactNode[]>([])
  const [webInterfaceTranslationFilesPreload, setWebInterfaceTranslationFilesPreload] = useState<ReactNode[]>([])

  let rolesList = ["None", "Blocked", "User", "Translator", "Remote Support", "Shop", "Administrator", "Global Administrator"]

  useEffect(() => {
    dispatchUserState({
      availableSpeechTranslationFiles: user.availableSpeechTranslationFiles,
      availableWebInterfaceTranslationFiles: user.availableWebInterfaceTranslationFiles,
      availableIBTGTranslationFiles: user.availableIBTGTranslationFiles,
      companies: user.companies,
      company: user.company,
      serialNumbers: user.serialNumbers,
      manufacturer: user.manufacturer,
      role: user.role
    })
  }, [user])

  useEffect(() => {
    const inputType = dialogState.inputType.toLowerCase()
    if (inputType.includes('select')) {
      setDialogInput(
        <FormControl sx={{ width: "100%", mt: 3, mb: 3 }}>
          <Select
            multiple={dialogState.inputType.toLowerCase().includes('multiple')}
            value={dialogState.inputType.toLowerCase().includes('multiple') ? dialogState.itemValue ? dialogState.itemValue.split(dialogState.separator) : [] : dialogState.itemValue}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                return "None"
              }
              return selected.join(", ")
            }}
            onChange={(event) => {
              const { target: { value } } = event
              let newValue = ''
              if (typeof value !== 'string') {
                // [S.S.] Multiple roles have been selected.
                if (value[value.length - 1] === "None") {
                  newValue = "None"
                } else if (value[value.length - 1] === "Blocked") {
                  newValue = "Blocked"
                } else if (value[value.length - 1] === "Global Administrator") {
                  newValue = "Global Administrator"
                } else {
                  let index = value.indexOf("None") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  index = value.indexOf("Blocked") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  index = value.indexOf("Global Administrator") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  newValue = value.join(dialogState.separator)
                }
                if (newValue === "") {
                  newValue = "None"
                }
              } else {
                newValue = value
              }
              dispatchDialogState({itemValue: newValue})
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: mode === 'light' ? "#fff" : "#151515",
                  borderRadius: '10px',
                  '&::-webkit-scrollbar': {
                    width: ".3em",
                  },
                  '&::-webkit-scrollbar-track': {
                    background: "transparent",
                    boxShadow: "inset 0 0 10px #1c1c1c",
                    borderRadius: "5px",
                    borderLeft: "1px solid transparent",
                    borderRight: "1px solid transparent",
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#444',
                    outline: '1px solid #444',
                    borderRadius: ".15em"
                  }
                }
              }
            }}
            input={<OutlinedInput />}
          >
            {
              dialogState.itemsList !== undefined ?
                dialogState.property === 'Companies' ?
                  distributorsListPreload
                : dialogState.property === 'Speech' ?
                  speechTranslationFilesPreload
                : dialogState.property === 'WebInterface' ?
                  webInterfaceTranslationFilesPreload
                :
                  dialogState.itemsList.map((item: string) => (
                    <MenuItem
                      key={item}
                      value={item}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&:hover": {
                          backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                        }
                      }}
                    >
                      {item}
                    </MenuItem>
                  ))
              : null
            }
          </Select>
        </FormControl>
      )
    } else if (inputType === 'textfield') {
      setDialogInput(
        <div
          style={{
            width: "100%",
            marginTop: "1em",
            marginBottom: "1em",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {
            dialogState.itemIsValid ||
              <p style={{ color: "#f53636", margin: 0 }}>
                Invalid list!
              </p>
          }
          <TextField
            sx={{ marginTop: "1em" }}
            label={dialogState.inputLabel}
            value={dialogState.itemValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newItemValue = event.target.value
              if (dialogState.inputRegex !== "") {
                let regex = new RegExp(dialogState.inputRegex);
                if (newItemValue.match(regex) || newItemValue === "") {
                  dispatchDialogState({itemIsValid: true})
                } else {
                  dispatchDialogState({itemIsValid: false})
                }
              }
              dispatchDialogState({itemValue: newItemValue})
            }}
          />
        </div>
      )
    } else if (inputType === 'phone') {
      setDialogInput(
        <div
          style={{
            marginTop: '1.5em',
            display: 'flex',
            gap: '1em'
          }}
        >
          <PhoneInput
            placeholder={dialogState.inputLabel}
            value={dialogState.itemValue}
            onChange={(newValue: string | undefined) => {
              dispatchDialogState({itemValue: newValue === undefined ? "" : newValue})
            }}
          />
        </div>
      )
    } else if (inputType === 'none') {
      setDialogInput(null)
    }
    // eslint-disable-next-line
  }, [dialogState.itemValue, dialogState.itemsList, dialogState.inputType])

  useEffect(() => {
    setDistributorsListPreload(distributors.map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&:hover": {
            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
          }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [distributors, mode])

  useEffect(() => {
    setSpeechTranslationFilesPreload([...new Set([...speechTranslationFilesFW2, ...speechTranslationFilesFW3])].sort().map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&:hover": {
            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
          }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [speechTranslationFilesFW2, speechTranslationFilesFW3, mode])

  useEffect(() => {
    setWebInterfaceTranslationFilesPreload([...new Set([...webInterfaceTranslationFilesFW2, ...webInterfaceTranslationFilesFW3])].sort().map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&:hover": {
            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
          }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [webInterfaceTranslationFilesFW2, webInterfaceTranslationFilesFW3, mode])

  return (
    <>
      {
        user.removed === 'false' ?
          displayMode === 'grid' ?
            <Box className={classes.userCard}>
              <div
                style={{
                  width: "100%",
                  marginInline: "1em",
                  display: "flex",
                  justifyContent: "flex-end"
                }}
              >
                <Icon>
                  {
                    user.signedIn ?
                      <RadioButtonCheckedRounded color="primary" />
                    :
                      <RadioButtonUncheckedRounded />
                  }
                </Icon>
              </div>
              <Box className={classes.userPhotoContainer}>
                {
                  !!user ?
                    user!.photoURL ?
                      <img
                        referrerPolicy="no-referrer"
                        className={classes.userPhoto}
                        src={user!.photoURL}
                        alt={user!.displayName !== '' ? user!.displayName : "Unknown"}
                      />
                    :
                      <NoPhotographyRounded fontSize="large" />
                  :
                    "loading"
                }
              </Box>
              <PropertyArea
                property={"Name"}
                content={`${user.displayName || "Unknown"}`}
                classes={{
                  container:  classes['propertyArea'],
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
                actionButton={true}
                actionButtonsList={[
                  {
                    tooltip: "Edit name",
                    disabled: (user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator",
                    onClick: () => {
                      dispatchDialogState({
                        dialogActionButtonLabel: "Save",
                        title: "Edit name field",
                        description: "Insert a name to be displayed for this user.",
                        property: "Name",
                        inputType: "textField",
                        inputLabel: "Display Name",
                        inputRegex: "",
                        saveButtonLoading: false,
                        socketSignal: 'set_display_name',
                        itemValue: user.displayName,
                        itemIsValid: true
                      })
                      dispatchUserState({
                        dialogOpen: true
                      })
                    },
                    icon: <EditRounded />
                  }
                ]}
              />
              <PropertyArea
                property={"Email"}
                content={`${user.email}`}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
              <PropertyArea
                property={"Company"}
                content={user.company ? user.company : "None"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
                actionButton={true}
                actionButtonsList={[
                  {
                    tooltip: "Edit company",
                    disabled: (user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator",
                    onClick: () => {
                      dispatchDialogState({
                        dialogActionButtonLabel: "Save",
                        title: "Edit company field",
                        description: "Insert a company to be displayed for this user.",
                        property: "Company",
                        inputType: "textField",
                        inputLabel: "Company",
                        inputRegex: "",
                        saveButtonLoading: false,
                        socketSignal: 'set_company',
                        itemValue: user.company,
                        itemIsValid: true
                      })
                      dispatchUserState({
                        dialogOpen: true
                      })
                    },
                    icon: <EditRounded />
                  }
                ]}
              />
              <PropertyArea
                property={"Companies"}
                content={`${user.companies ? user.companies.split(";").join("\n") : "None"}`}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
                actionButton={true}
                actionButtonsList={[
                  {
                    tooltip: "Edit companies",
                    disabled: (user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator",
                    onClick: () => {
                      dispatchDialogState({
                        dialogActionButtonLabel: "Save",
                        title: "Choose companies",
                        description: "Choose companies to be managed by this user.",
                        property: "Companies",
                        inputType: "multipleSelect",
                        saveButtonLoading: false,
                        socketSignal: 'set_companies',
                        separator: ';',
                        itemsList: [], // this is already preloaded to improve performance
                        itemValue: userState.companies,
                        itemIsValid: true
                      })
                      dispatchUserState({
                        dialogOpen: true
                      })
                    },
                    icon: <EditRounded />
                  }
                ]}
              />
              {
                (currentRole === "Global Administrator" && user.role.includes("Remote Support")) &&
                  <MultiplePropertiesArea
                    topMargin={true}
                    property={"Remote Support"}
                    classes={classes}
                    children={
                      <>
                        <PropertyArea
                          key={0}
                          property={"Companies"}
                          content={`${user.companies ? user.companies.split(";").join("\n") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit companies",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose companies",
                                  description: "Choose companies to be managed by this user.",
                                  property: "Companies",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_companies',
                                  separator: ';',
                                  itemsList: [], // this is already preloaded to improve performance
                                  itemValue: userState.companies,
                                  itemIsValid:  true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                        <PropertyArea
                          key={1}
                          property={"Embossers"}
                          content={`${user.role.includes("Administrator") ? "All" : userState.serialNumbers ? userState.serialNumbers.split(",").join(", ") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit embossers",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose embossers",
                                  description: "Choose embossers to be managed by this user.",
                                  property: "Embossers",
                                  inputType: "textField",
                                  inputLabel: "Serial Numbers",
                                  inputRegex: "^([0-9]{5},)*[0-9]{5}$",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_serial_numbers',
                                  itemValue: userState.serialNumbers,
                                  itemIsValid: true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                        <PropertyArea
                          key={2}
                          property={"Manufacturers"}
                          content={`${user.role.includes("Administrator") ? "All" : userState.manufacturer ? userState.manufacturer.split(",").join(", ") : "All"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit manufacturers",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose manufacturers",
                                  description: "Choose manufacturers to be displayed to this user.",
                                  property: "Manufacturers",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_manufacturer',
                                  separator: ',',
                                  itemsList: manufacturersList,
                                  itemValue: userState.manufacturer,
                                  itemIsValid:  true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                      </>
                    }
                  />
              }
              {
                (currentRole === "Global Administrator" && user.role.includes("Translator")) &&
                  <MultiplePropertiesArea
                    topMargin={true}
                    property={"Translation"}
                    classes={classes}
                    children={
                      <>
                        <PropertyArea
                          key={3}
                          property={"Speech Translation Files"}
                          content={`${user.availableSpeechTranslationFiles ? user.availableSpeechTranslationFiles.split(";").join("\n") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit speech translation files",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose speech translation files",
                                  description: "Choose speech translation files to be managed by this user.",
                                  property: "Speech",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_available_speech_translation_files',
                                  separator: ';',
                                  itemsList: [],
                                  itemValue: userState.availableSpeechTranslationFiles,
                                  itemIsValid:  true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                        <PropertyArea
                          key={4}
                          property={"Web Interface Translation Files"}
                          content={`${user.availableWebInterfaceTranslationFiles ? user.availableWebInterfaceTranslationFiles.split(";").join("\n") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit web interface translation files",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose web interface translation files",
                                  description: "Choose web interface translation files to be managed by this user.",
                                  property: "WebInterface",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_available_web_interface_translation_files',
                                  separator: ';',
                                  itemsList: [],
                                  itemValue: userState.availableWebInterfaceTranslationFiles,
                                  itemIsValid:  true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                        <PropertyArea
                          key={5}
                          property={"IBTG Translation Files"}
                          content={`${user.availableIBTGTranslationFiles ? user.availableIBTGTranslationFiles.split(";").join("\n") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit IBTG translation files",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose IBTG translation files",
                                  description: "Choose IBTG translation files to be managed by this user.",
                                  property: "IBTG",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_available_ibtg_translation_files',
                                  separator: ';',
                                  itemsList: IBTGTranslationFiles,
                                  itemValue: userState.availableIBTGTranslationFiles,
                                  itemIsValid:  true
                                })
                                dispatchUserState({
                                  dialogOpen: true
                                })
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                      </>
                    }
                  />
              }
              <PropertyArea
                property={"Role"}
                content={`${user.role !== "" ? user.role.split(';').join('\n') : "\n"}`}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
                actionButton={true}
                actionButtonsList={[
                  {
                    tooltip: "Edit role",
                    disabled: (user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.id === currentUser!.uid,
                    onClick: () => {
                      dispatchDialogState({
                        dialogActionButtonLabel: "Save",
                        title: "Choose user's role",
                        description: "Choose a role for this user.",
                        property: "Role",
                        inputType: "multipleSelect",
                        saveButtonLoading: false,
                        socketSignal: 'set_role',
                        separator: ';',
                        itemsList: rolesList,
                        itemValue: userState.role,
                        itemIsValid:  true
                      })
                      dispatchUserState({
                        dialogOpen: true
                      })
                    },
                    icon: <EditRounded />
                  }
                ]}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: ".5em"
                }}
              >
                <Button
                  variant="contained"
                  className={classes.userCardButtonOpen}
                  onClick={() => {
                    setSelectedUser(user)
                  }}
                  size="small"
                >
                  Open
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={(user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator"}
                  className={classes.userCardButtonRemove}
                  onClick={() => {
                    dispatchDialogState({
                      dialogActionButtonLabel: "Remove",
                      title: "Remove user",
                      description: "Are you sure you want to remove this user?",
                      inputType: "none",
                      socketSignal: 'remove_user'
                    })
                    dispatchUserState({
                      dialogOpen: true
                    })
                  }}
                  size="small"
                >
                  Remove
                </Button>
              </Box>
            </Box>
          :
            <Box className={classes.userRow}>
              <Icon>
                {
                  user.signedIn ?
                    <RadioButtonCheckedRounded color="primary" />
                  :
                    <RadioButtonUncheckedRounded />
                }
              </Icon>
              <PropertyArea
                property={"Name"}
                content={`${user.displayName || "Unknown"}`}
                classes={{
                  container: classes['userRowPropertyArea'],
                  content: classes['userRowPropertyAreaContent']
                }}
                actionButton={true}
                actionButtonsList={[
                  {
                    tooltip: "Edit name",
                    disabled: (user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator",
                    onClick: () => {
                      dispatchDialogState({
                        dialogActionButtonLabel: "Save",
                        title: "Edit name field",
                        description: "Insert a name to be displayed for this user.",
                        property: "Name",
                        inputType: "textField",
                        inputLabel: "Display Name",
                        inputRegex: "",
                        saveButtonLoading: false,
                        socketSignal: 'set_display_name',
                        itemValue: user.displayName,
                        itemIsValid: true
                      })
                      dispatchUserState({
                        dialogOpen: true
                      })
                    },
                    icon: <EditRounded />
                  }
                ]}
              />
              <PropertyArea
                property={"Email"}
                content={`${user.email}`}
                classes={{
                  container: classes['userRowPropertyArea'],
                  content: classes['userRowPropertyAreaContent']
                }}
              />
              <Button
                variant="contained"
                className={classes.userRowButtonOpen}
                onClick={() => {
                  setSelectedUser(user)
                }}
                size="small"
                sx={{
                  fontSize: `${width > 750 ? '.8em' : '.7em'}`
                }}
              >
                Open
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={(user.role.includes("Administrator") && !(currentRole === "Global Administrator")) || user.role === "Global Administrator"}
                className={classes.userRowButtonRemove}
                onClick={() => {
                  dispatchDialogState({
                    dialogActionButtonLabel: "Remove",
                    title: "Remove user",
                    description: "Are you sure you want to remove this user?",
                    inputType: "none",
                    socketSignal: 'remove_user'
                  })
                  dispatchUserState({
                    dialogOpen: true
                  })
                }}
                size="small"
                sx={{
                  fontSize: `${width > 750 ? '.8em' : '.7em'}`
                }}
              >
                Remove
              </Button>
            </Box>
        : null
      }

      <Dialog
        open={userState.dialogOpen}
        PaperProps={{
          style: {
            backgroundColor: mode === 'light' ? "#f6f6f6" : "#151515",
            borderRadius: "10px",
            boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)"
          }
        }}
        keepMounted={false}
        onClose={() => dispatchUserState({
          dialogOpen: false
        })}
        fullWidth
        maxWidth="xs"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogState.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogState.description}
          </DialogContentText>
          {dialogInput}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              dispatchUserState({
                dialogOpen: false
              })
            }}
            color="error"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={dialogState.saveButtonLoading}
            onClick={async () => {
              dispatchDialogState({saveButtonLoading: true})
              if (dialogState.property === "Role") {
                const changeRole = httpsCallable(functions, 'changeRole')
                await changeRole({ email: user.email!, role: dialogState.itemValue })
              }
              socket.emit(`${dialogState.socketSignal}`, user.id!, dialogState.itemValue)
              dispatchDialogState({
                saveButtonLoading: false
              })
              dispatchUserState({
                dialogOpen: false
              })
            }}
            autoFocus
            color="secondary"
            disabled={!dialogState.itemIsValid}
          >
            {dialogState.dialogActionButtonLabel}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}